import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { callApi } from '@/hooks/useFetch';
import i18n from '@/i18n';
import { toast } from '@/lib/toast';
import {
  EmoiMessage,
  EmoiToolReceipt,
  useEmoiChatStore,
} from '@/stores/emoiChatStore';
import { useBackgroundTaskStore } from '@/stores/backgroundTaskStore';
import { useRouteContext } from './useRouteContext';

interface EmoiChatResponse {
  assistant_message: string;
  tool_calls_executed: EmoiToolReceipt[];
  cache_keys_to_invalidate: string[][];
  job_ids: string[];
}

function newId(): string {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useEmoiSend() {
  const queryClient = useQueryClient();
  const routeContext = useRouteContext();
  const appendMessage = useEmoiChatStore((s) => s.appendMessage);
  const patchMessage = useEmoiChatStore((s) => s.patchMessage);
  const setIsSending = useEmoiChatStore((s) => s.setIsSending);
  const isSending = useEmoiChatStore((s) => s.isSending);
  const reset = useEmoiChatStore((s) => s.reset);
  const addBgTask = useBackgroundTaskStore((s) => s.addTask);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return;

      const userMsg: EmoiMessage = {
        id: newId(),
        role: 'user',
        content: trimmed,
        createdAt: Date.now(),
      };
      const placeholderId = newId();
      const placeholder: EmoiMessage = {
        id: placeholderId,
        role: 'assistant',
        content: '',
        pending: true,
        createdAt: Date.now(),
      };
      appendMessage(userMsg);
      appendMessage(placeholder);
      setIsSending(true);

      try {
        const data = await callApi<EmoiChatResponse>('/emoi/chat', {
          method: 'POST',
          data: {
            message: trimmed,
            route_context: { page: routeContext.page, entity_ids: routeContext.entityIds },
            language: i18n.language || 'en',
          },
        });

        patchMessage(placeholderId, {
          content: data.assistant_message || '',
          toolReceipts: data.tool_calls_executed,
          pending: false,
        });

        for (const key of data.cache_keys_to_invalidate || []) {
          queryClient.invalidateQueries({ queryKey: key });
        }

        for (const jobId of data.job_ids || []) {
          addBgTask({
            jobId,
            title: 'emoi: background job',
            status: 'polling',
          });
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Request failed';
        patchMessage(placeholderId, {
          content: `⚠ ${msg}`,
          pending: false,
        });
      } finally {
        setIsSending(false);
      }
    },
    [
      addBgTask,
      appendMessage,
      isSending,
      patchMessage,
      queryClient,
      routeContext.entityIds,
      routeContext.page,
      setIsSending,
    ],
  );

  const clear = useCallback(async () => {
    if (isSending) return;
    try {
      await callApi('/emoi/clear', { method: 'POST' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Request failed';
      toast.error(i18n.t('emoi.errorPrefix'), {
        description: msg,
      });
    } finally {
      reset();
    }
  }, [isSending, reset]);

  return { send, clear, isSending };
}
